import { type IResult, UAParser } from 'ua-parser-js';

/*
  When run in headless environment, USER_AGENT_PARSED returns:
  {
    ua: 'Mozilla/5.0 (darwin) AppleWebKit/537.36 (KHTML, like Gecko) jsdom/11.12.0',
    browser: { name: 'WebKit', version: '537.36', major: '537' },
    engine: { name: 'WebKit', version: '537.36' },
    os: { name: undefined, version: undefined },
    device: { vendor: undefined, model: undefined, type: undefined },
    cpu: { architecture: undefined }
  }
 */

export const parsedUserAgent = (userAgentString: string | undefined) => {
  if (!userAgentString) return new UAParser().getResult();
  return new UAParser(userAgentString).getResult();
};

const browserNameIncludes = (userAgent: IResult, name: string) =>
  !!userAgent.browser.name && userAgent.browser.name.includes(name);

const osNameIncludes = (userAgent: IResult, name: string) =>
  !!userAgent.os.name && userAgent.os.name.includes(name);

export const isDesktop = (userAgentString: string | undefined): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  const userAgentOsName = userAgent.os.name;

  if (!userAgentOsName) return false;

  const isWindows = userAgentOsName.indexOf('Windows') !== -1;
  const isMac = userAgentOsName.indexOf('Mac') !== -1;
  return isWindows || isMac;
};

export const isMobile = (userAgentString: string | undefined): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  const isMobileDevice = userAgent.device.type === 'mobile';
  return isMobileDevice || !isDesktop;
};

export const isRetina = (): boolean => window.devicePixelRatio > 1;

export const isFacebookInApp = (
  userAgentString: string | undefined,
): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  const browserName = userAgent.browser.name || '';
  return browserName.indexOf('Facebook') !== -1;
};

export const isIOS12 = (userAgentString: string | undefined): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  const osName = userAgent.os.name;
  const osVersion = userAgent.os.version;

  if (!osName || !osVersion) return false;

  return osName === 'iOS' && osVersion.startsWith('12');
};

export const isChrome = (userAgentString: string | undefined): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  return browserNameIncludes(userAgent, 'Chrome');
};

export const isSafari = (userAgentString: string | undefined): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  return browserNameIncludes(userAgent, 'Safari');
};

export const isFirefox = (userAgentString: string | undefined): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  return browserNameIncludes(userAgent, 'Firefox');
};

export const isIOS = (userAgentString: string | undefined): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  return osNameIncludes(userAgent, 'iOS');
};

export const isAndroid = (userAgentString: string | undefined): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  return osNameIncludes(userAgent, 'Android');
};

export const isWindows = (userAgentString: string | undefined): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  return osNameIncludes(userAgent, 'Windows');
};

export const isMacOS = (userAgentString: string | undefined): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  return osNameIncludes(userAgent, 'Mac');
};

export const isEmbeddediOS = (userAgentString: string | undefined): boolean => {
  const standAlone =
    'standalone' in window.navigator && Boolean(window.navigator.standalone);
  return isIOS(userAgentString) && standAlone && isSafari(userAgentString);
};

export const isEmbeddedAndroid = (
  userAgentString: string | undefined,
): boolean => {
  const userAgent = parsedUserAgent(userAgentString);
  return userAgent.ua.includes('wv');
};

export const isEmbeddedWebView = (
  userAgentString: string | undefined,
): boolean => {
  if (!isMobile(userAgentString)) return false;

  return isEmbeddediOS(userAgentString) || isEmbeddedAndroid(userAgentString);
};
